import { useEffect, useState } from "react";

import {
  OrderSortOptions,
  SortDirection,
} from "@hl/shared-features/lib/apollo/graphql.generated";
import {
  useOwnerOffersQuery,
  useTokenOffersQuery,
} from "@hl/shared-features/lib/features/marketplace/queries/marketplace.graphql.generated";
import { useCollections, useListings } from "@reservoir0x/reservoir-kit-ui";

import { usePublicUserProfileQuery } from "~features/MintPage/queries.graphql.generated";
import useMintState from "~hooks/useMintState";

import {
  CollectionContractType,
  NftContractStandard,
  SeriesToken,
} from "../../../apollo/graphql.generated";

export type MarketplaceState = ReturnType<typeof useMarketplace>;

export const useMarketplace = (
  token?: Omit<SeriesToken, "attributes">,
  onTokenRefresh?: () => void
) => {
  const [listingSource, setListingSource] = useState<string>();
  const { address, chainId, collection, reservoirCollectionId, walletAddress } =
    useMintState();
  const is1155 = collection?.standard === NftContractStandard.ERC1155;
  const tokenId = token?.tokenId;
  const ownerAddress = token?.ownerAddress;
  const userBalance = token?.userBalance;
  const [burnedByAddress, setBurnedByAddress] = useState<string>();

  const { data: collectionMarketData } = useCollections({
    id: reservoirCollectionId,
  });

  const {
    data: ownerListings,
    mutate: refetchOwnerListings,
    isLoading: isLoadingOwnerListings,
  } = useListings(
    {
      token: `${address}:${tokenId}`,
      status: "active",
      maker: walletAddress,
      sortBy: "price",
      sortDirection: "asc",
      normalizeRoyalties: false,
    },
    undefined,
    !!walletAddress && !!address && !!tokenId
  );

  const {
    data: listings,
    isFetchingInitialData: isLoadingListings,
    mutate: refetchListings,
  } = useListings(
    {
      token: `${address}:${tokenId}`,
      status: "active",
      sortBy: "price",
      sortDirection: "asc",
      normalizeRoyalties: false,
    },
    undefined,
    !!address && !!tokenId
  );

  const {
    data: bids,
    loading: isLoadingBids,
    refetch: refetchBids,
  } = useTokenOffersQuery({
    variables: {
      chainId,
      createdAfter: null,
      tokenId: tokenId ?? "",
      tokenAddress: address,
      aboveFloor: null,
      sortDirection: SortDirection.DESC,
      sortBy: OrderSortOptions.PRICE,
      after: null,
      first: 10,
      isImportedCollection:
        collection?.type === CollectionContractType.Imported,
    },
    skip: !tokenId,
  });

  const {
    data: ownerBids,
    loading: isLoadingOwnerBids,
    refetch: refetchOwnerBids,
  } = useOwnerOffersQuery({
    variables: {
      chainId,
      createdAfter: null,
      tokenId: tokenId ?? null,
      tokenAddress: address,
      aboveFloor: null,
      tokenOrCollectionName: null,
      sortDirection: SortDirection.DESC,
      sortBy: OrderSortOptions.PRICE,
      after: null,
      first: 10,
      criteriaKind: null,
      minPrice: null,
      floorPriceDifference: null,
    },
    skip: !tokenId || !walletAddress,
  });

  const { data: tokenOwnerProfile, loading: isLoadingTokenOwnerProfile } =
    usePublicUserProfileQuery({
      variables: {
        slug: ownerAddress || "",
      },
      skip: is1155 ? !ownerAddress : !(walletAddress && userBalance),
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    setListingSource(listings?.[0]?.source?.domain as string);
  }, [listings]);

  return {
    listings,
    isLoadingListings,
    refetchListings,
    ownerListings,
    isLoadingOwnerListings,
    refetchOwnerListings,
    bids: bids?.getTokenOffers.edges ?? [],
    isLoadingBids,
    refetchBids,
    ownerBids: ownerBids?.getOwnerOffers.edges ?? [],
    isLoadingOwnerBids,
    refetchOwnerBids,
    collectionMarketData: collectionMarketData[0],
    refetchToken: onTokenRefresh,
    listingSource,
    tokenOwnerProfile: tokenOwnerProfile?.getPublicAccountSettings,
    isLoadingTokenOwnerProfile,
    burnedByAddress,
    setBurnedByAddress,
  };
};
